import { useState, useEffect, useMemo, useContext, useCallback, useRef } from "react";

// analytics
import {
    impressionCartHeaderLottieBolt,
    impressionCartHeaderLottieExpress,
    impressionCartHeaderLottieOtg,
} from "@cart-client/analytics/events/cartHeaderLottie";
import { AnalyticsContext, CartOverlaysContext } from "@swiggy-private/checkout-core";

// context
import { CartContext } from "@cart-client/contexts/CartContext";
import { INITIAL_CART_HEADER_CONTEXT_VALUE } from "@cart-client/contexts/CartHeaderContext";

// helpers
import { isCartHeaderTypeLottie, processCartData } from "@cart-client/helpers/cartHeaderLottie";
import { isEmpty } from "@swiggy-private/js-utils";

// constants
import { CART_STATUS_CODES } from "@swiggy-private/cart-ui";
import { OverlayType, GlobalOverlayPriority } from "@swiggy-private/cart-types";
import {
    hideTheme,
    applyFrequencyCappingLogic,
    updateStateForLottieDisplay,
    isStateEqual,
    startAnimation,
} from "./helper";
import { useSkipAnimationFinalState } from "./useSkipAnimationFinalState";

enum LottieIds {
    CART_HEADER_LOTTIE = "CART_HEADER_LOTTIE",
}

// custom hook
export const useCartHeaderLottieRenderer = () => {
    const { cartState } = useContext(CartContext);
    const { impressionEvent } = useContext(AnalyticsContext);
    const { registerOverlays, enabledOverlayId, onCloseOverlay } = useContext(CartOverlaysContext);
    const cartResponse = cartState.reviewedCart;
    const [blckStateUpdated, setBlckStateUpdated] = useState(false);
    const [boltStateUpdated, setBoltStateUpdated] = useState(false);
    const isCartHeaderTypeRef = useRef(false);
    const isSuccess = useMemo(() => cartResponse?.statusCode === CART_STATUS_CODES.SUCCESS, [cartResponse?.statusCode]);
    const isLottieRegisteredRef = useRef(false);
    const isBlckStateShownRef = useRef(false);
    const isBoltStateShownRef = useRef(false);
    const flag = useRef(false);
    const timeoutRef = useRef<NodeJS.Timeout>();
    const [cartHeaderState, setCartHeaderState] = useState({
        ...INITIAL_CART_HEADER_CONTEXT_VALUE.cartHeaderState,
        isStatusSuccess: isSuccess,
    });

    const cartType = cartResponse?.data?.cart_header_widget_presentation?.cart_type;
    const isCartHeaderType = useMemo(() => isCartHeaderTypeLottie(cartType ?? ""), [cartType]);
    const processedCartData = useMemo(() => processCartData(cartResponse), [cartResponse]);
    const {
        isBlck,
        isOtg,
        isExpress,
        rxId,
        cartHeaderSessionObject,
        sessionFreqCapKey,
        frequencyData,
        sid,
        isBolt,
        cartLocalStorageKey,
        boltUpdateKey,
        skipAnimationShowFinalState,
    } = processedCartData;
    const sessionCount = useMemo(
        () => frequencyData?.frequency_cap.session_count || 0,
        [frequencyData?.frequency_cap.session_count],
    );
    const count = useMemo(() => frequencyData?.frequency_cap.count || 0, [frequencyData?.frequency_cap.count]);
    const freqCapId = useMemo(() => frequencyData?.frequency_cap.id || "", [frequencyData?.frequency_cap.id]);
    const shouldShowLottie = useMemo(
        () => (isBlck && (isExpress || isOtg)) || isBolt,
        [isBlck, isExpress, isOtg, isBolt],
    );
    const hasNecessaryCartData = useMemo(
        () => cartResponse && shouldShowLottie && rxId && sid && !isEmpty(cartHeaderSessionObject),
        [cartResponse, shouldShowLottie, rxId, sid, cartHeaderSessionObject],
    );
    const shouldHideThemeInitially = useMemo(
        () => !isSuccess || !shouldShowLottie || (!frequencyData && !isBolt),
        [isSuccess, shouldShowLottie, frequencyData, isBolt],
    );
    let shouldHideTheme = useMemo(() => cartHeaderState.hideTheme, [cartHeaderState.hideTheme]);
    const currentValue = cartHeaderSessionObject[sessionFreqCapKey];
    const isBoltRxFrequencyCapReached = useMemo(
        () => isBolt && currentValue === 1 && isSuccess,
        [isBolt, currentValue, isSuccess],
    );
    const isBlckRxFrequencyCapReached = useMemo(
        () => currentValue === 1 && frequencyData && (isExpress || isOtg) && isSuccess,
        [isOtg, isExpress, frequencyData, currentValue, isSuccess],
    );
    const isBoltParamsUpdated = isBolt ? boltUpdateKey !== cartHeaderSessionObject["rxSla"] : false;

    const sendAnalytics = useCallback(() => {
        if (isExpress) {
            impressionCartHeaderLottieExpress(impressionEvent, count, (cartHeaderSessionObject[freqCapId] || 0) + 1);
        }
        if (isBolt) {
            const context = JSON.stringify({
                cartId: cartResponse?.data?.cartId,
                isBolt: isBolt,
                isBlack: isBlck,
                isBlackWithOtg: isBlck && isOtg,
            });
            impressionCartHeaderLottieBolt(impressionEvent, context);
        }
        if (!isBolt && isOtg) {
            impressionCartHeaderLottieOtg(impressionEvent, count, (cartHeaderSessionObject[freqCapId] || 0) + 1);
        }
    }, [
        isBlck,
        isExpress,
        isOtg,
        isBolt,
        impressionEvent,
        count,
        cartHeaderSessionObject,
        freqCapId,
        cartResponse?.data?.cartId,
    ]);
    if (isCartHeaderType) {
        isCartHeaderTypeRef.current = true;
    } else {
        isCartHeaderTypeRef.current = false;
    }
    if (isSuccess) {
        shouldHideTheme = false;
    }
    // rx level frequency capping
    if (hasNecessaryCartData && !isCartHeaderTypeRef.current) {
        if (isBoltRxFrequencyCapReached && !boltStateUpdated) {
            isBoltStateShownRef.current = true;
            setCartHeaderState((prev) => ({
                ...prev,
                showFinalState: true,
                themeChange: true,
                isBlack: isBlck,
                isExpress: false,
                isOtg: isOtg,
                showAnimation: true,
                isBolt: isBolt,
            }));
            setBoltStateUpdated(true);
            if (enabledOverlayId === LottieIds.CART_HEADER_LOTTIE) {
                onCloseOverlay();
            }
        } else if (isBlckRxFrequencyCapReached && !blckStateUpdated) {
            isBlckStateShownRef.current = true;
            setCartHeaderState((prev) => ({
                ...prev,
                showFinalState: true,
                themeChange: true,
                isBlack: isBlck,
                isExpress: isExpress,
                isOtg: isOtg,
                showAnimation: true,
                isBolt: false,
            }));
            setBlckStateUpdated(true);
            if (enabledOverlayId === LottieIds.CART_HEADER_LOTTIE) {
                onCloseOverlay();
            }
        }
    }

    useEffect(() => {
        flag.current = enabledOverlayId === LottieIds.CART_HEADER_LOTTIE;
    }, [enabledOverlayId]);

    useEffect(() => {
        // Reset state flags when success status changes
        if (!isSuccess || isCartHeaderTypeRef.current) {
            setBoltStateUpdated(false);
            setBlckStateUpdated(false);
            isBoltStateShownRef.current = false;
            isBlckStateShownRef.current = false;
            isLottieRegisteredRef.current = false;
            return;
        }
        // Reset state flags when cart type changes
        if (!isBolt) {
            setBoltStateUpdated(false);
            isBoltStateShownRef.current = false;
        }
        if (!(isBlck && (isExpress || isOtg))) {
            setBlckStateUpdated(false);
            isBlckStateShownRef.current = false;
        }
        isLottieRegisteredRef.current = false;
    }, [isBolt, isBlck, isExpress, isOtg, isSuccess, isCartHeaderTypeRef]);

    // Handle skipAnimationShowFinalState: Show final state (gradient) directly without animation
    // This is used when magic items lottie has not played yet in the session
    useSkipAnimationFinalState({
        skipAnimationShowFinalState,
        shouldShowLottie,
        isSuccess,
        isBlck,
        isOtg,
        isExpress,
        isBolt,
        enabledOverlayId,
        onCloseOverlay,
        setCartHeaderState,
        setBoltStateUpdated,
        setBlckStateUpdated,
        isBoltStateShownRef,
        isBlckStateShownRef,
    });

    useEffect(() => {
        // Skip if skipAnimationShowFinalState is true - handled by separate useIsomorphicLayoutEffect
        if (
            !cartResponse ||
            (isBolt && !isBoltParamsUpdated) ||
            isCartHeaderTypeRef.current ||
            skipAnimationShowFinalState
        ) {
            return;
        }
        const newCartHeaderState = { ...cartHeaderState };
        let isThemeHidden = shouldHideTheme;
        if (shouldHideThemeInitially) {
            // Preconditions for hiding the theme: non 0 status, non-Black membership, blck but not otg or express, or missing frequency data and not bolt cart.
            Object.assign(newCartHeaderState, hideTheme());
            isThemeHidden = true;
        }
        if (
            !isThemeHidden &&
            !isLottieRegisteredRef.current &&
            ((isBlck && !isBlckStateShownRef.current) || (isBolt && isBoltParamsUpdated))
        ) {
            registerOverlays({
                overlayType: OverlayType.LOTTIE,
                priority: GlobalOverlayPriority.LOTTIE_ANIMATION,
                id: LottieIds.CART_HEADER_LOTTIE,
            });
            isLottieRegisteredRef.current = true;
            flag.current = true;
        }

        let updatedState = newCartHeaderState;
        let updatedThemeHidden = isThemeHidden;

        if (flag.current) {
            const frequencyResult = applyFrequencyCappingLogic({
                newCartHeaderState,
                isThemeHidden,
                frequencyData,
                cartHeaderSessionObject,
                sessionFreqCapKey,
                sessionCount,
                rxId,
                sid,
                INITIAL_CART_HEADER_CONTEXT_VALUE,
                cartLocalStorageKey,
                sendAnalytics,
                isBolt,
                boltUpdateKey,
            });
            updatedState = frequencyResult.updatedState;
            updatedThemeHidden = frequencyResult.isThemeHidden;
            if (updatedThemeHidden) {
                onCloseOverlay();
            }
        }
        const finalState = updateStateForLottieDisplay({
            newCartHeaderState: updatedState,
            isThemeHidden: updatedThemeHidden,
            isBlck,
            isOtg,
            isExpress,
            isSuccess,
            isBolt,
        });
        if (!isStateEqual(cartHeaderState, finalState)) {
            setCartHeaderState(finalState);
            if (finalState.isBolt) {
                setBoltStateUpdated(true);
            } else {
                setBlckStateUpdated(true);
            }
        }

        if (enabledOverlayId === LottieIds.CART_HEADER_LOTTIE) {
            // clear the timeout if it exists
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            startAnimation({
                newCartHeaderState: finalState,
                setCartHeaderState,
                frequencyData,
                timeoutRef,
            });
        }

        return () => {
            // clear the timeout if it exists
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cartResponse, enabledOverlayId, skipAnimationShowFinalState]);

    const cartHeaderContextValue = useMemo(
        () => ({
            cartHeaderState,
            setCartHeaderState,
        }),
        [cartHeaderState],
    );

    return cartHeaderContextValue;
};
